import * as React                    from 'react';

import { PhotoProvider             } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import { DownloadVector            } from 'src/components/vector/download';
import { ReloadVector              } from 'src/components/vector/reload';
import { EyeShowVector             } from 'src/components/vector/eye-show';
import { SharingVector             } from 'src/components/vector/sharing';

import { Typography                } from 'src/components/common/typography';
import { Labeled                   } from 'src/components/common/labeled';
import { TabSwitch                 } from 'src/components/common/tab-switch';
import { Switch                    } from 'src/components/common/switch';
import { IconButton                } from 'src/components/common/icon-button';
import { ReferSelectorItem         } from 'src/components/features/common/refer-selector';

import { AttachmentPhoto           } from 'src/components/features/inspection/view/attachment-photo';
import { AttachmentDOC             } from 'src/components/features/inspection/view/attachment-doc';
import { StaticValue               } from 'src/components/features/inspection/view/static-value';

import { PieceFragmentLookAndFeel, 
         Container,
         FormContainer,
         FormRowElement,
         PhotoList,
         Hr,
         PieceFragmentVariants,
         piecefragment_variants    } from 'src/components/features/inspection/view/piece-fragment/ui';

import * as types                    from 'src/services/api/types';
import { DefectType,
         ProductTypeKolpino,
         QCState                   } from 'src/services/api/types/inspection';

import { AppDispatch,
         useAppSelector, 
         useAppDispatch,
         storeApi                  } from 'src/store';

import { Nullable                  } from 'src/common';



const TYPE_GENERAL = types.inspection.PieceFileType.GENERAL;
const TYPE_PHOTO = types.inspection.PieceFileType.PHOTO;
const TYPE_PDF = types.inspection.PieceFileType.PDF;
const MODE_DEL = types.inspection.PieceFileMode.DEL;

const isTopographyExpandedCalc = (piece: Nullable<types.inspection.Piece>): boolean => {
  if (piece !== null)
  {
    const {
      defectDistanceFromEdge,
      defectSize,
      defectStep,
      defectTpgIdDistance,
      defectTpgIdSize,
      defectTpgIdStep,
      defectTpgTitleDistance,
      defectTpgTitleSize,
      defectTpgTitleStep,
    } = piece.topography;

    return (
      (defectDistanceFromEdge !== null && defectDistanceFromEdge !== 0)
      ||
      (defectSize !== null && defectSize !== 0)
      ||
      (defectStep !== null && defectStep !== 0)
      ||
      (defectTpgIdDistance !== null && defectTpgIdDistance !== 0)
      ||
      (defectTpgIdSize !== null && defectTpgIdSize !== 0)
      ||
      (defectTpgIdStep !== null && defectTpgIdStep !== 0)
      ||
      (defectTpgTitleDistance !== null && defectTpgTitleDistance.trim() !== '')
      ||
      (defectTpgTitleSize !== null && defectTpgTitleSize.trim() !== '')
      ||
      (defectTpgTitleStep !== null && defectTpgTitleStep.trim() !== '')
    )
  }

  return false;
}

type PieceFragmentElement = { lookAndFeel: PieceFragmentLookAndFeel; dispatch: AppDispatch; };

const InputUEModeSwitcher: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isPiece = piece?.isPiece ?? false;
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;

  const items = [
    { id: 'id', label: 'Идентификац. №' },
    { id: 'piece', label: account.isWireRod ? '№ партии/плавки' : '№ плавки' }
  ];
  const item = isPiece ? items[1] : items[0];

  return (
    <React.Fragment>
      <TabSwitch
        variant = { lookAndFeel.modeSwitcherVariant }
        items = { items }
        value = { item }
        onChange = { (_) => { } }
      />
    </React.Fragment>
  )
}

const ProductType: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);

  if ((piece?.qmetData?.productType ?? '') === '')
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Вид продукции'>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.qmetData?.productType ?? '' }</StaticValue>
    </Labeled>
  )
}

const DirtyWagons: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isError = (piece?.carriageQcs ?? []).every((qc) => !qc.isControl);

  return (
    <React.Fragment>
      <Labeled variant = { lookAndFeel.labeledVariant } label = 'Номер вагона'>
        <StaticValue
          variant = {isError ? lookAndFeel.staticValueErrorVariant : lookAndFeel.staticValueVariant }
        >
          { piece?.carriage ?? '' }
        </StaticValue>
      </Labeled>
      {(piece?.carriageQcs ?? []).map((cert, index) => (
        <React.Fragment>
          <Hr lookAndFeel = { lookAndFeel } />
          <Typography variant = { lookAndFeel.topoDWCert }>{ `Сертификат качества ${index + 1}` }</Typography>
          <Labeled variant = { lookAndFeel.labeledVariant } label = '№ сертификата качества' isRequired>
            <StaticValue
              variant = { cert.isControl ? lookAndFeel.staticValueVariant : lookAndFeel.staticValueErrorVariant }
            >
              { cert.qcNum ?? '' }
            </StaticValue>
          </Labeled>
        </React.Fragment>
      ))}
    </React.Fragment>
  )
}

const Term: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isError = (piece?.specificationPoses ?? []).every((pos) => !pos.isControl);

  const { Full, Not, Partial } = types.inspection.PieceMetalDelivered;
  const referItems: ReferSelectorItem[] = [
    { id: `${Full}`, label: 'Полностью' },
    { id: `${Partial}`, label: 'Частично' },
    { id: `${Not}`, label: 'Не доставлен' },
  ]

  return (
    <React.Fragment>
      <Labeled variant = { lookAndFeel.labeledVariant } label = 'Номер спецификации'>
        <StaticValue
          variant = {isError ? lookAndFeel.staticValueErrorVariant : lookAndFeel.staticValueVariant }
        >
          { piece?.specificationNum ?? '' }
        </StaticValue>
      </Labeled>
      <FormRowElement lookAndFeel = { lookAndFeel }>
        <Typography variant = { lookAndFeel.topoSwitchLabelTypo }>Весь заказ</Typography>
        <Switch
          variant = { lookAndFeel.topoSwitchVariant }
          value = { piece?.isAllPoses ?? false }
          onChange = { (_) => { } }
        />
      </FormRowElement>
      {(piece?.isAllPoses ?? false) && (
        <Labeled variant = { lookAndFeel.labeledVariant } label = 'Металл доставлен'>
          <StaticValue variant = { lookAndFeel.staticValueVariant }>
            { referItems.find((item) => item.id === `${piece?.metalDelivered}`)?.label ?? '' }
          </StaticValue>
        </Labeled>
      )}
      {!(piece?.isAllPoses ?? false) && (piece?.specificationPoses ?? []).map((position, index) => (
        <React.Fragment>
          <Hr lookAndFeel = { lookAndFeel } />
          <Typography variant = { lookAndFeel.topoDWCert }>{ `Позиция ${index + 1}` }</Typography>
          <Labeled variant = { lookAndFeel.labeledVariant } label = 'Позиция'>
            <StaticValue
              variant = { position.isControl ? lookAndFeel.staticValueVariant : lookAndFeel.staticValueErrorVariant }
            >
              { position.pos ?? '' }
            </StaticValue>
          </Labeled>
          <Labeled variant = { lookAndFeel.labeledVariant } label = 'Металл доставлен'>
            <StaticValue variant = { lookAndFeel.staticValueVariant }>
              { referItems.find((item) => item.id === `${position.metalDelivered}`)?.label ?? '' }
            </StaticValue>
          </Labeled>
          <Labeled variant = { lookAndFeel.labeledVariant } label = 'Объем, не поставленный в срок'>
            <StaticValue
              variant = { position.isControl ? lookAndFeel.staticValueVariant : lookAndFeel.staticValueErrorVariant }
            >
              { position.volumeDeliveredTime ?? '' }
            </StaticValue>
          </Labeled>
          <Labeled variant = { lookAndFeel.labeledVariant } label = 'Продажная единица'>
            <StaticValue
              variant = { position.isControl ? lookAndFeel.staticValueVariant : lookAndFeel.staticValueErrorVariant }
            >
              { position.unit.title ?? '' }
            </StaticValue>
          </Labeled>
          <Labeled variant = { lookAndFeel.labeledVariant } label = 'Кол-во дней просрочки'>
            <StaticValue
              variant = { position.isControl ? lookAndFeel.staticValueVariant : lookAndFeel.staticValueErrorVariant }
            >
              { position.daysOverdue ?? '' }
            </StaticValue>
          </Labeled>
        </React.Fragment>
      ))}
      <Hr lookAndFeel = { lookAndFeel } />
    </React.Fragment>
  )
}

const QmetId: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Идентификационный №'>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.qmetId ?? '' }</StaticValue>
    </Labeled>
  )
}

const Heat: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = '№ плавки'>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.heat ?? '' }</StaticValue>
    </Labeled>
  )
}

const Piece: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isKolpino = useAppSelector(storeApi.auth.common.selectors.selectIsKolpino);
  
  let label = '';

  if (isKolpino)
  {
    label = '№ листа';
  }
  else
  {
    label = '№ рулона/листа/трубы';
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = { label }>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.piece ?? '' }</StaticValue>
    </Labeled>
  )
}

const GroupAtt: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;

  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isKolpino = useAppSelector(storeApi.auth.common.selectors.selectIsKolpino);
  
  const isWireRod = account.isWireRod || (piece?.groupAtt ?? null) !== null;

  let isHide = false;
  let label = '';

  if (isKolpino)
  {
    isHide = false;
    label = '№ партии по маркировке';
  }
  else
  {
    isHide = !isWireRod;
    label = '№ партии аттестации';
  }

  if (isHide)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = { label }>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.groupAtt ?? '' }</StaticValue>
    </Labeled>
  )
}

const QCNum: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;

  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const inspectionGuid = useAppSelector(storeApi.inspection.view.selectors.selectInspectionGuid);
  const qcState = piece?.qmetData?.qcState ?? QCState.NONE;
  const qcNum = (piece?.qcNum ?? '') || (piece?.qmetData?.qcNum ?? '');
  const isSmc = piece?.isSmc ?? false;
  const isWireRod = account.isWireRod || (piece?.groupAtt ?? null) !== null;
  const productTypeKolpinoId = piece?.productTypeKolpinoId ?? null;
  const isKolpino = useAppSelector(storeApi.auth.common.selectors.selectIsKolpino);

  let isHide = false;
  let isAllowCertDownload = false;

  if (isKolpino)
  {
    isHide = false;
    isAllowCertDownload = qcNum !== '' && productTypeKolpinoId === ProductTypeKolpino.UNPROCESSED_SHEET;
  }
  else
  {
    isHide = !isWireRod && qcNum === '';
    isAllowCertDownload = qcNum !== '' && !isSmc;
  }

  const checkCertRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (isAllowCertDownload && qcState === QCState.LOADING) {
      if (!checkCertRef.current) {
        checkCertRef.current = true;
        dispatch(storeApi.inspection.view.async.checkCertAsync({ inspectionGuid }));
      }
    }
  }, []);

  const downloadHandler = () => {
    dispatch(storeApi.inspection.view.async.downloadCertAsync({ qcNum }));
  }

  const reloadHandler = () => {
    dispatch(storeApi.inspection.view.async.checkCertAsync({ inspectionGuid }));
  }

  const showHandler = () => {
    dispatch(storeApi.inspection.view.async.viewCertAsync({ qcNum }));
  }

  const sharingHandler = () => {
    dispatch(storeApi.inspection.view.async.shareCertAsync({ qcNum }));
  }

  if (isHide)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = '№ сертификата качества'>
      <FormRowElement lookAndFeel = { lookAndFeel }>
        <StaticValue variant = { lookAndFeel.staticValueVariant }>{ qcNum }</StaticValue>
        { isAllowCertDownload && qcState === QCState.NONE && 
          <IconButton variant = { lookAndFeel.loadCertButtonBariant } isTapAllowed onTap = { downloadHandler }>
            <DownloadVector />
          </IconButton>
        }
        { isAllowCertDownload && qcState === QCState.LOADING && 
          <IconButton variant = { lookAndFeel.loadCertButtonBariant } isTapAllowed onTap = { reloadHandler }>
            <ReloadVector />
          </IconButton>
        }
        { isAllowCertDownload && qcState === QCState.LOADED && 
          <React.Fragment>
            <IconButton variant = { lookAndFeel.loadCertButtonBariant } isTapAllowed onTap = { showHandler }>
              <EyeShowVector />
            </IconButton>
            <IconButton variant = { lookAndFeel.loadCertButtonBariant } isTapAllowed onTap = { sharingHandler }>
              <SharingVector />
            </IconButton>
          </React.Fragment>
        }
      </FormRowElement>
    </Labeled>
  )
}

const KolpinoProductType: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Вид продукции'>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.productTypeKolpinoTitle ?? '' }</StaticValue>
    </Labeled>
  )
}

const KolpinoDrawNum: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const productTypeKolpinoId = piece?.productTypeKolpinoId ?? null;

  const { PROCESSED_SHEET, UNPROCESSED_SHEET } = ProductTypeKolpino;

  const isHide = productTypeKolpinoId === null || [PROCESSED_SHEET, UNPROCESSED_SHEET].includes(productTypeKolpinoId);

  if (isHide)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = '№ чертежа'>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.drawNum ?? '' }</StaticValue>
    </Labeled>
  )
}

const CustomFields: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const customFields = piece?.fields ?? [];

  const fields = [...account.fields].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <React.Fragment>
      {fields.map((field) => (
        <Labeled 
          key = { field.code }
          variant = { lookAndFeel.labeledVariant }
          label = { field.title }
        >
          <StaticValue variant = { lookAndFeel.staticValueVariant }>{ customFields.find((item) => item.code === field.code)?.value ?? '' }</StaticValue>
        </Labeled>
      ))}
    </React.Fragment>
  )
}

const Weight: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const weight = piece?.weight ?? '';
  const unit = piece?.unit?.title ?? 'кг';

  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  return (
    <Labeled
      variant = { lookAndFeel.labeledVariant }
      label = { isEntryAccounting ? 'Фактический вес' : 'Количество брака' }
    >
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ `${weight} ${weight === '' ? '' : unit}` }</StaticValue>
    </Labeled>
  )
}

const Note: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Детальное описание по позиции (прочие комментарии)'>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.note ?? '' }</StaticValue>
    </Labeled>
  )
}

const Photo: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);

  const files = piece?.files ?? [];
  
  const general = files.find((file) => file.type === TYPE_GENERAL) ?? null;
  const isGeneralExists = general !== null && general.mode !== MODE_DEL;
  const photos = files.filter((file) => file.type === TYPE_PHOTO && file.mode !== MODE_DEL);
  const pdfs = files.filter((file) => file.type === TYPE_PDF && file.mode !== MODE_DEL);

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Фотографии'>
      <PhotoList lookAndFeel = { lookAndFeel }>
        <PhotoProvider >
          {isGeneralExists && (
            <AttachmentPhoto
              variant = { lookAndFeel.attachmentPhotoVariant }
              photo = { general }
            />
          )}
          {photos.map((photo) => (
           <AttachmentPhoto
              variant = { lookAndFeel.attachmentPhotoVariant }
              photo = { photo }
            />
          ))}
          {pdfs.map((pdf) => (
           <AttachmentDOC
              variant = { lookAndFeel.attachmentPhotoVariant }
              doc = { pdf }
              source = 'piece'
            />
          ))}
        </PhotoProvider>
      </PhotoList>
    </Labeled>
  )
}

const IsTopography: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting)
  {
    return null;
  }

  return (
    <FormRowElement lookAndFeel = { lookAndFeel }>
      <Typography variant = { lookAndFeel.topoSwitchLabelTypo }>Топография дефекта</Typography>
      <Switch
        variant = { lookAndFeel.topoSwitchVariant }
        value = { isTopographyExpanded }
        onChange = { (_) => { } }
      />
    </FormRowElement>
  )
}

const TopographyNote: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <Typography variant = { lookAndFeel.topoNoteTypo }>
      Топографию дефекта заполнить<br /> ниже или сделать фото<br /> с рулеткой
    </Typography>
  )
}

const TopographySize: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <Labeled
      variant = { lookAndFeel.labeledVariant }
      label = 'Размер дефекта'
    >
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.topography?.defectSize ?? '' }</StaticValue>
    </Labeled>
  )
}

const TopographySizeId: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <React.Fragment>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.topography?.defectTpgTitleSize ?? '' }</StaticValue>
    </React.Fragment>
  )
}

const TopographyStep: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <Labeled
      variant = { lookAndFeel.labeledVariant }
      label = 'Шаг дефекта'
    >
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.topography?.defectStep ?? '' }</StaticValue>
    </Labeled>
  )
}

const TopographyStepId: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <React.Fragment>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.topography?.defectTpgTitleStep ?? '' }</StaticValue>
    </React.Fragment>
  )
}

const TopographyDistance: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <Labeled
      variant = { lookAndFeel.labeledVariant }
      label = 'Расстояние от кромки'
    >
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.topography?.defectDistanceFromEdge ?? '' }</StaticValue>
    </Labeled>
  )
}

const TopographyDistanceId: React.FC<PieceFragmentElement> = ({ lookAndFeel, dispatch }) => {
  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const isEntryAccounting = useAppSelector(storeApi.inspection.view.selectors.selectIsEntryAccounting);

  const isTopographyExpanded = React.useMemo(() => {
    return isTopographyExpandedCalc(piece);
  }, [piece]);

  if (isEntryAccounting || !isTopographyExpanded)
  {
    return null;
  }

  return (
    <React.Fragment>
      <StaticValue variant = { lookAndFeel.staticValueVariant }>{ piece?.topography?.defectTpgTitleDistance ?? '' }</StaticValue>
    </React.Fragment>
  )
}


type Props = {
  variant: PieceFragmentVariants;
}
export const PieceFragment: React.FC<Props> = ({
  variant,
}) => {
  const lookAndFeel = piecefragment_variants.get(variant)!;
  const dispatch = useAppDispatch();

  const piece = useAppSelector(storeApi.inspection.view.selectors.selectPieceSelected);
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;
  const defectGuid = useAppSelector(storeApi.inspection.view.selectors.selectDefectExpandedGuid);
  const defectType = useAppSelector((store) => storeApi.inspection.view.selectors.selectDefectDefectType(store, defectGuid));
  const isKolpino = useAppSelector(storeApi.auth.common.selectors.selectIsKolpino);

  const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
  const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
  const isTerm = defectType === DefectType.TERM;

  const isPiece = piece?.isPiece ?? false;

  return (
    <Container lookAndFeel = { lookAndFeel }>
      <FormContainer lookAndFeel = { lookAndFeel }>
        <Typography variant = 'blackw600s18lh24'>Информация</Typography>
        {isQualityOrService && (
          <React.Fragment>
            <InputUEModeSwitcher lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            {!isPiece && (
              <React.Fragment>
                <QmetId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <ProductType lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <Heat lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <Piece lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <QCNum lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
              </React.Fragment>
            )}
            {isPiece && (
              <React.Fragment>
                {isKolpino ?
                  <KolpinoProductType lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                  :
                  <ProductType lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                }
                <Heat lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <Piece lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <GroupAtt lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                <QCNum lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                {isKolpino && (
                  <KolpinoDrawNum lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {isDirtyWagons && (
          <DirtyWagons lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
        )}
        {isTerm && (
          <Term lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
        )}
        {isQualityOrService && (
          <React.Fragment>
            <CustomFields lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <Weight lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
          </React.Fragment>
        )}
        <Note lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
        {isQualityOrService && account.role !== types.auth.AccountRole.Contractor && (
          <React.Fragment>
            <IsTopography lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyNote lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographySize lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographySizeId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyStep lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyStepId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyDistance lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyDistanceId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
          </React.Fragment>
        )}
        <Photo lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
        {isQualityOrService && account.role === types.auth.AccountRole.Contractor && (
          <React.Fragment>
            <IsTopography lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyNote lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographySize lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographySizeId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyStep lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyStepId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyDistance lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
            <TopographyDistanceId lookAndFeel = { lookAndFeel } dispatch = { dispatch } />
          </React.Fragment>
        )}
      </FormContainer>
    </Container>
  );
};
